import React, { useState, useEffect } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const DailyWordsPage = ({ API, isDarkMode, showNotification, userToken, hasPaidPlan, setCurrentView, setInitialProfileTab }) => {
  const { t } = useTranslation();

  // App-shell caches: read synchronously so icons + cards + progress render
  // from frame 1 instead of the fallback-then-swap flicker on Ctrl+R.
  const _readCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  // Daily-only caches: invalidate when the user's local date changes (daily words
  // and learnedToday reset each day, so yesterday's cache would show wrong "Learned"
  // pills until the fresh fetch resolves — that's the button-swap the user reported).
  const _todayKey = new Date().toLocaleDateString('en-CA');
  const _readDailyCache = (key) => {
    try {
      const c = localStorage.getItem(key);
      if (!c) return null;
      const parsed = JSON.parse(c);
      if (parsed && parsed._date === _todayKey) return parsed.data;
    } catch {}
    return null;
  };
  const _writeDailyCache = (key, data) => {
    try { localStorage.setItem(key, JSON.stringify({ _date: _todayKey, data })); } catch {}
  };
  const _cachedDwSettings = _readCache('dwSettingsCache');
  const _cachedDailyWords = _readDailyCache('dwDailyWordsCache');
  const _cachedLearnedToday = _readDailyCache('dwLearnedTodayCache');
  const _cachedWeeklyLearning = _readCache('dwWeeklyLearningCache');

  // State
  const [dailyWords, setDailyWords] = useState(_cachedDailyWords || []);
  const [loadingDailyWords, setLoadingDailyWords] = useState(false);
  const [learnedToday, setLearnedToday] = useState(_cachedLearnedToday || { count: 0, goal: 3, learned_word_ids: [], goal_reached: false });
  const [showCelebration, setShowCelebration] = useState(false);
  const [showDailyWordsNotification, setShowDailyWordsNotification] = useState(false);
  const [weeklyLearning, setWeeklyLearning] = useState(_cachedWeeklyLearning);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [dwSettings, setDwSettings] = useState(_cachedDwSettings || {});

  // Load weekly learning data
  const loadWeeklyLearning = async () => {
    setLoadingWeekly(true);
    try {
      const userLocalDate = new Date().toLocaleDateString('en-CA');
      const response = await fetch(`${API}/user/words/learned/weekly?user_date=${userLocalDate}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWeeklyLearning(data);
        try { localStorage.setItem('dwWeeklyLearningCache', JSON.stringify(data)); } catch {}
      }
    } catch (error) {
      console.error('Error loading weekly learning:', error);
    } finally {
      setLoadingWeekly(false);
    }
  };

  // Load today's learned words progress
  const loadLearnedProgress = async () => {
    try {
      const userLocalDate = new Date().toLocaleDateString('en-CA');
      const response = await fetch(`${API}/user/words/learned/today?user_date=${userLocalDate}`, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLearnedToday(data);
        _writeDailyCache('dwLearnedTodayCache', data);

        // Show notification at most once per day (per-browser localStorage flag).
        // Avoids the "harsh popup on every page load" complaint from earlier review.
        const hasUnlearnedWords = data.count < data.goal;
        const today = new Date().toLocaleDateString('en-CA');
        let alreadyShownToday = false;
        try { alreadyShownToday = localStorage.getItem('daily_words_popup_last_shown') === today; } catch {}
        if (hasUnlearnedWords && !alreadyShownToday) {
          setTimeout(() => {
            setShowDailyWordsNotification(true);
          }, dwSettings.notification_delay_ms || 2000);
        }
      }
    } catch (error) {
      console.error('Error loading learned progress:', error);
    }
  };

  // Mark word as learned
  const markWordAsLearned = async (word) => {
    try {
      const englishText = word.sourceText || word.english;
      const darijaText = word.translatedText || word.darija;
      const userLocalDate = new Date().toLocaleDateString('en-CA');

      const formData = new FormData();
      formData.append('word_id', word.id);
      formData.append('english', englishText);
      formData.append('darija', darijaText);
      formData.append('user_date', userLocalDate);

      const response = await fetch(`${API}/user/words/learned`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${userToken}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();

        if (data.already_learned) {
          showNotification(t('daily_already_learned'), 'info');
        } else {
          // Trigger fly animation
          const wordElement = document.querySelector(`[data-word-id="${word.id}"]`);
          if (wordElement) {
            wordElement.classList.add('word-fly-animation');
          }

          showNotification(`✨ ${englishText} ${t('daily_flying_to_calendar')}`, 'success');

          // Wait for animation to complete
          await new Promise(resolve => setTimeout(resolve, 800));

          // Reload both progress AND weekly calendar
          await loadLearnedProgress();
          await loadWeeklyLearning();
          await loadDailyWords();

          // Check if goal reached
          if (data.today_count >= learnedToday.goal) {
            setShowCelebration(true);
            setTimeout(() => setShowCelebration(false), dwSettings.celebration_dismiss_ms || 5000);
          }
        }
      } else {
        showNotification(t('daily_words_mark_failed'), 'error');
      }
    } catch (error) {
      console.error('Error marking word as learned:', error);
      showNotification(t('daily_words_mark_failed'), 'error');
    }
  };

  // Load daily words for learning
  const loadDailyWords = async () => {
    if (!userToken) return;

    setLoadingDailyWords(true);
    try {
      const mood = 'general';
      const userLocalDate = new Date().toLocaleDateString('en-CA');
      const url = `${API}/daily-words/${mood}?user_date=${userLocalDate}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${userToken}` }
      });

      if (response.ok) {
        const data = await response.json();
        const words = data.words || [];
        setDailyWords(words);
        _writeDailyCache('dwDailyWordsCache', words);
      } else {
        const errorData = await response.json();
        showNotification('Failed to load daily words: ' + (errorData.detail || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error loading daily words:', error);
      showNotification('Error loading daily words: ' + error.message, 'error');
    } finally {
      setLoadingDailyWords(false);
    }
  };

  // Load Daily Words page settings
  useEffect(() => {
    const loadDwSettings = async () => {
      try {
        const res = await fetch(`${API}/daily-words/settings`);
        if (res.ok) {
          const data = await res.json();
          setDwSettings(data);
          try { localStorage.setItem('dwSettingsCache', JSON.stringify(data)); } catch {}
        }
      } catch {}
    };
    loadDwSettings();
  }, [API]);

  // Load data on mount
  useEffect(() => {
    if (userToken) {
      loadDailyWords();
      loadLearnedProgress();
      loadWeeklyLearning();
    }
  }, [userToken]);

  return (
    <div className={`min-h-screen pb-24 overflow-x-hidden ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 backdrop-blur-md ${isDarkMode ? 'bg-gray-900/95 border-gray-700' : 'bg-white/95 border-gray-200'} border-b`}>
        <div className="max-w-6xl mx-auto px-4 py-3">
          {setCurrentView && (
            <button
              onClick={() => setCurrentView('translator')}
              className={`flex items-center gap-1 mb-2 px-2 py-1 rounded-lg text-sm font-medium transition-all ${isDarkMode ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('dashboard_back')}
            </button>
          )}
          <div className="flex items-center gap-3">
            {dwSettings.header_icon_url
              ? <img src={`${API.replace('/api', '')}${dwSettings.header_icon_url}`} alt="" className="w-8 h-8 object-contain" />
              : <span className="text-2xl">{'\ud83d\udcda'}</span>}
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('nav_daily_words')}
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* Daily Words Notification Popup - PRO ONLY */}
        {showDailyWordsNotification && hasPaidPlan && learnedToday.count < learnedToday.goal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-3 md:p-4">
            <div className={`relative max-w-sm md:max-w-md w-full rounded-xl md:rounded-2xl p-3 md:p-8 shadow-2xl ${isDarkMode ? 'bg-gradient-to-br from-purple-900 via-pink-900 to-blue-900' : 'bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100'} border-2 md:border-4 border-yellow-400`}>
              <button
                onClick={() => {
                  setShowDailyWordsNotification(false);
                  try { localStorage.setItem('daily_words_popup_last_shown', new Date().toLocaleDateString('en-CA')); } catch {}
                }}
                className={`absolute top-1 right-1 md:top-4 md:right-4 w-7 h-7 md:w-10 md:h-10 rounded-full flex items-center justify-center text-base md:text-2xl hover:scale-110 transition-transform ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'} text-white`}
              >
                ✖
              </button>
              <div className="text-center">
                <div className="text-3xl md:text-6xl mb-1 md:mb-4">
                  {dwSettings.notification_icon_url
                    ? <img src={`${API.replace('/api', '')}${dwSettings.notification_icon_url}`} alt="" className="w-12 h-12 md:w-20 md:h-20 object-contain mx-auto" />
                    : '\ud83c\udfaf'}
                </div>
                <h3 className={`text-base md:text-3xl font-bold mb-1 md:mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {t('new_words_today')}
                </h3>
                <div className={`text-3xl md:text-6xl font-black mb-1 md:mb-4 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-500'}`}>
                  {learnedToday.goal - learnedToday.count}
                </div>
                <p className={`text-sm md:text-xl mb-3 md:mb-6 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {t('words_waiting_below')} {'\ud83d\udc47'}
                </p>
                <button
                  onClick={() => {
                    setShowDailyWordsNotification(false);
                    try { localStorage.setItem('daily_words_popup_last_shown', new Date().toLocaleDateString('en-CA')); } catch {}
                    setTimeout(() => {
                      const widget = document.querySelector('[data-daily-words-widget]');
                      if (widget) {
                        widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }, 300);
                  }}
                  className="w-full px-3 md:px-6 py-2 md:py-4 bg-gradient-to-r from-yellow-400 via-orange-500 to-pink-500 text-white font-bold rounded-lg md:rounded-xl hover:from-yellow-500 hover:via-orange-600 hover:to-pink-600 transition-all text-sm md:text-xl shadow-lg"
                >
                  {t('start_learning')} {'\ud83d\ude80'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Modal */}
        {showCelebration && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`relative max-w-md w-full rounded-2xl p-8 text-center ${isDarkMode ? 'bg-gradient-to-br from-green-900 via-blue-900 to-purple-900' : 'bg-gradient-to-br from-green-100 via-blue-100 to-purple-100'}`}>
              <button
                onClick={() => setShowCelebration(false)}
                className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full text-lg font-bold transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-800 hover:bg-black/10'}`}
              >&times;</button>
              <div className="text-8xl mb-4 animate-bounce">
                {dwSettings.celebration_icon_url
                  ? <img src={`${API.replace('/api', '')}${dwSettings.celebration_icon_url}`} alt="" className="w-24 h-24 object-contain mx-auto" />
                  : '\ud83c\udf89'}
              </div>
              <h3 className={`text-3xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {t('goal_achieved')}
              </h3>
              <p className={`text-xl mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {t('you_learned_words_today').replace('{count}', learnedToday.count)} {dwSettings.goal_star_icon_url
                  ? <img src={`${API.replace('/api', '')}${dwSettings.goal_star_icon_url}`} alt="" className="w-6 h-6 object-contain inline" />
                  : '\ud83c\udf1f'}
              </p>
              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('keep_up_amazing_work')}
              </p>
              <button
                onClick={() => setShowCelebration(false)}
                className="mt-6 px-6 py-3 bg-gradient-to-r from-green-500 to-blue-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-blue-700 transition-all"
              >
                {t('continue_learning')} {'\ud83d\udcaa'}
              </button>
            </div>
          </div>
        )}

        {/* Daily Words Widget - PREMIUM FEATURE */}
        <div data-daily-words-widget className={`relative overflow-hidden rounded-xl border-2 ${
          !hasPaidPlan
            ? isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-100 border-gray-300'
            : isDarkMode ? 'bg-gradient-to-br from-blue-900/40 via-sky-900/40 to-indigo-900/40 border-blue-500/30' : 'bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 border-blue-300'
        }`}>
          {!hasPaidPlan && (
            <div className="absolute inset-0 bg-black/10 backdrop-blur-sm z-10 flex items-center justify-center cursor-pointer" onClick={() => { setCurrentView('profile'); setInitialProfileTab('membership'); }}>
              <div className={`p-6 rounded-xl text-center ${isDarkMode ? 'bg-gray-900/90' : 'bg-white/90'} shadow-2xl max-w-md`}>
                <div className="text-6xl mb-4">
                  {dwSettings.pro_lock_icon_url
                    ? <img src={`${API.replace('/api', '')}${dwSettings.pro_lock_icon_url}`} alt="" className="w-16 h-16 object-contain mx-auto" />
                    : '\ud83d\udd12'}
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('pro_feature')}
                </h3>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('unlock_daily_words')}
                </p>
                <button className="px-6 py-3 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white font-bold rounded-lg hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105">
                  {'\u2b50'} {t('upgrade_to_pro')}
                </button>
              </div>
            </div>
          )}
          <div className={`relative ${!hasPaidPlan ? 'blur-sm pointer-events-none' : ''}`}>
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-300 opacity-10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-400 opacity-10 rounded-full -ml-12 -mb-12"></div>

            <div className="relative p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">
                    {dwSettings.widget_icon_url
                      ? <img src={`${API.replace('/api', '')}${dwSettings.widget_icon_url}`} alt="" className="w-10 h-10 object-contain" />
                      : '\ud83c\udfaf'}
                  </div>
                  <div>
                    <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {t('todays_learning_words')}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {t('daily_goal')} {learnedToday.count}/{learnedToday.goal} {t('words_learned_today')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {learnedToday.count < learnedToday.goal && (
                    <button
                      onClick={() => setShowDailyWordsNotification(true)}
                      className="px-2 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-base font-semibold bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white transition-all animate-pulse"
                    >
                      {'\ud83d\udd14'} {t('remind_me')}
                    </button>
                  )}
                  <button
                    onClick={loadDailyWords}
                    disabled={loadingDailyWords}
                    className={`px-2 py-1.5 md:px-4 md:py-2 rounded-lg text-xs md:text-base font-semibold transition-all ${
                      loadingDailyWords
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                    }`}
                  >
                    🔄
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className={`w-full h-4 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-500 flex items-center justify-center text-xs font-bold text-white"
                    style={{ width: `${(learnedToday.count / learnedToday.goal) * 100}%` }}
                  >
                    {learnedToday.count > 0 && `${Math.round((learnedToday.count / learnedToday.goal) * 100)}%`}
                  </div>
                </div>
              </div>

              {loadingDailyWords && dailyWords.length === 0 ? (
                /* Skeleton only when no cached words yet. With cached cards visible, refresh runs silently. */
                <div className="space-y-3 animate-pulse">
                  {[1,2,3].map(i => (
                    <div key={i} className={`h-20 md:h-24 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
                  ))}
                </div>
              ) : learnedToday.count >= learnedToday.goal && learnedToday.count > 0 ? (
                <div className={`text-center py-12 px-6 rounded-xl ${isDarkMode ? 'bg-gradient-to-r from-green-900/40 to-blue-900/40 border-2 border-green-500' : 'bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-400'}`}>
                  <div className="text-7xl mb-4 animate-bounce">
                    {dwSettings.goal_star_icon_url
                      ? <img src={`${API.replace('/api', '')}${dwSettings.goal_star_icon_url}`} alt="" className="w-20 h-20 object-contain mx-auto" />
                      : '\ud83c\udf1f'}
                  </div>
                  <h3 className={`text-2xl md:text-3xl font-extrabold mb-3 ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                    {dwSettings.goal_complete_text || <>{'\ud83c\udf89'} {t('goal_achieved')} {t('you_learned_words_today').replace('{count}', learnedToday.count)} {'\ud83c\udf1f'}</>}
                  </h3>
                  <p className={`text-lg md:text-xl font-semibold mb-4 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                    {t('keep_up_amazing_work')}
                  </p>
                  <p className={`text-base ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                    {'\u2705'} {t('come_back_tomorrow').replace('{count}', learnedToday.goal)}
                  </p>
                  <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    {t('words_reset_daily')}
                  </p>
                </div>
              ) : dailyWords.length > 0 ? (
                <div className="space-y-3">
                  {dailyWords.map((word) => {
                    const isLearned = learnedToday.learned_word_ids.includes(word.id);
                    return (
                      <div
                        key={word.id}
                        data-word-id={word.id}
                        className={`p-4 md:p-5 rounded-2xl border shadow-sm hover:shadow-md transition-all ${
                          isLearned
                            ? isDarkMode
                              ? 'bg-green-900/30 border-green-700 opacity-75'
                              : 'bg-green-50 border-green-400 opacity-75'
                            : isDarkMode
                              ? 'bg-gradient-to-br from-gray-800/50 to-blue-900/20 border-gray-700 hover:border-blue-500'
                              : 'bg-gradient-to-br from-white to-blue-50/50 border-blue-100 hover:border-blue-400'
                        }`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-lg md:text-2xl font-bold ${isLearned ? 'line-through' : ''} ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                {word.sourceText || word.english}
                              </span>
                              {(word.audioUrl || word.audio_url) && (word.audioUrl || word.audio_url).trim() !== '' && (
                                <button
                                  onClick={() => {
                                    const audio = new Audio(word.audioUrl || word.audio_url);
                                    audio.play();
                                    showNotification(t('daily_playing_audio'), 'info');
                                  }}
                                  className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded-full text-xs md:text-sm font-semibold transition-all flex items-center gap-1"
                                  title="Play pronunciation"
                                >
                                  🔊 {t('play_audio')}
                                </button>
                              )}
                            </div>
                            <p className={`text-base md:text-xl ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                              {word.translatedText || word.darija}
                            </p>
                            {(word.latinScript || word.transliteration) && (
                              <p className={`text-xs md:text-sm italic mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ({word.latinScript || word.transliteration})
                              </p>
                            )}
                          </div>
                          <div className="flex md:flex-col items-center md:items-end gap-2 mt-2 md:mt-0">
                            {isLearned ? (
                              <span className="px-3 md:px-4 py-1.5 md:py-2 bg-green-500 text-white rounded-full text-xs md:text-base font-semibold flex items-center gap-1">
                                {'\u2713'} {t('learned')}
                              </span>
                            ) : (
                              <button
                                onClick={() => markWordAsLearned(word)}
                                className="flex-1 md:flex-none px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white rounded-full text-xs md:text-base font-semibold transition-all shadow-sm whitespace-nowrap"
                              >
                                {'\u2713'} {t('mark_learned')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Dynamic Progress Message */}
                  {learnedToday.count > 0 && (
                    <div className={`text-center p-4 rounded-lg ${
                      learnedToday.goal_reached
                        ? isDarkMode ? 'bg-green-900/30 border-2 border-green-500' : 'bg-green-100 border-2 border-green-400'
                        : isDarkMode ? 'bg-blue-900/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-300'
                    }`}>
                      <p className={`text-lg font-bold ${
                        learnedToday.goal_reached
                          ? isDarkMode ? 'text-green-300' : 'text-green-700'
                          : isDarkMode ? 'text-blue-300' : 'text-blue-700'
                      }`}>
                        🎯 {t('goal_achieved')} {t('you_learned_words_today').replace('{count}', learnedToday.count)}
                      </p>
                      {!learnedToday.goal_reached && (
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          {'\ud83d\udcaa'} {learnedToday.goal - learnedToday.count} {t('more_to_reach_goal')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className={`text-center py-8 px-4 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <div className="text-6xl mb-4">{'\ud83d\udcda'}</div>
                  <p className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {t('no_words_available')}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('check_back_soon_daily')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Weekly Learning Calendar - PREMIUM FEATURE */}
        <div className={`p-4 md:p-6 rounded-2xl border-2 shadow-2xl relative ${
          !hasPaidPlan
            ? isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-100 border-gray-300'
            : isDarkMode ? 'bg-gradient-to-br from-blue-900/40 via-purple-900/40 to-indigo-900/40 border-blue-500/30' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50 border-blue-300'
        }`}>
          {!hasPaidPlan && (
            <div className="absolute inset-0 bg-black/10 backdrop-blur-sm z-10 flex items-center justify-center cursor-pointer" onClick={() => { setCurrentView('profile'); setInitialProfileTab('membership'); }}>
              <div className={`p-6 rounded-xl text-center ${isDarkMode ? 'bg-gray-900/90' : 'bg-white/90'} shadow-2xl max-w-md`}>
                <div className="text-6xl mb-4">
                  {dwSettings.pro_lock_icon_url
                    ? <img src={`${API.replace('/api', '')}${dwSettings.pro_lock_icon_url}`} alt="" className="w-16 h-16 object-contain mx-auto" />
                    : '\ud83d\udd12'}
                </div>
                <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {t('pro_feature')}
                </h3>
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('track_weekly_progress')}
                </p>
                <button className="px-6 py-3 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white font-bold rounded-lg hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 transition-all transform hover:scale-105">
                  {'\u2b50'} {t('upgrade_to_pro')}
                </button>
              </div>
            </div>
          )}
          <div className={!hasPaidPlan ? 'blur-sm pointer-events-none' : ''}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h4 className={`text-xl md:text-3xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                <span className="block md:inline">
                  {dwSettings.weekly_icon_url
                    ? <img src={`${API.replace('/api', '')}${dwSettings.weekly_icon_url}`} alt="" className="w-6 h-6 md:w-8 md:h-8 object-contain inline" />
                    : '\ud83d\udcc5'}{' '}
                  {t('your_learning_this_week')}
                </span>
                {weeklyLearning && weeklyLearning.total_this_week > 0 && (
                  <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg animate-pulse ml-0 md:ml-2 mt-2 md:mt-0">
                    {weeklyLearning.total_this_week} {t('words')}
                  </span>
                )}
              </h4>
              <p className={`text-sm md:text-base mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {weeklyLearning ? (
                  <>
                    {weeklyLearning.total_this_week === 0 ? (
                      <span className="flex items-center gap-1">{'\ud83c\udfaf'} {t('start_journey_today')}</span>
                    ) : (
                      <span className="flex items-center gap-1">
                        {'\ud83c\udf89'} {t('great_progress_resets')}
                      </span>
                    )}
                  </>
                ) : t('loading_progress')}
              </p>
            </div>
          </div>

          {/* How it works info */}
          <div className={`flex items-start gap-2.5 p-3 rounded-xl mb-4 ${isDarkMode ? 'bg-blue-900/20 border border-blue-800/40' : 'bg-blue-50 border border-blue-200/60'}`}>
            <span className="text-base mt-0.5">
              {dwSettings.info_icon_url
                ? <img src={`${API.replace('/api', '')}${dwSettings.info_icon_url}`} alt="" className="w-4 h-4 object-contain" />
                : '\ud83d\udca1'}
            </span>
            <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              {t('daily_words_info_text')}
            </p>
          </div>

          {weeklyLearning && weeklyLearning.daily_breakdown ? (
            <div className="flex flex-wrap gap-3 md:gap-4 md:justify-center">
              {Object.entries(weeklyLearning.daily_breakdown).map(([day, data]) => {
                const today = new Date().toLocaleDateString('en-CA');
                const isToday = data.date === today;

                return (
                  <div
                    key={day}
                    // NOTE: Use md:min-h not md:h — cards must grow with content on desktop. Do NOT add max-height or overflow-hidden.
                    className={`w-full sm:w-[calc(50%-6px)] md:w-[calc(25%-12px)] md:min-h-[260px] p-4 rounded-xl border-2 transition-all transform hover:scale-105 hover:shadow-xl ${
                      isToday
                        ? isDarkMode
                          ? 'border-blue-400 bg-gradient-to-br from-blue-900/60 to-purple-900/60 shadow-lg shadow-blue-500/20'
                          : 'border-blue-500 bg-gradient-to-br from-blue-100 to-purple-100 shadow-lg shadow-blue-300/50'
                        : isDarkMode
                          ? 'border-gray-600 bg-gray-800/70 hover:border-gray-500'
                          : 'border-gray-300 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`text-sm font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {day.substring(0, 3).toUpperCase()}
                      </div>
                      {isToday && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-500 text-white animate-pulse">
                          {t('today')}
                        </span>
                      )}
                    </div>
                    <div className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {new Date(data.date + 'T00:00:00').toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                    </div>

                    {data.count > 0 ? (
                      <>
                        <div className={`text-3xl font-extrabold mb-3 ${isDarkMode ? 'text-green-400' : 'text-green-600'} flex items-center gap-1`}>
                          {data.count}
                          <span className="text-lg">✨</span>
                        </div>
                        <div className="space-y-1.5">
                          {data.words.map((word, i) => (
                            <div
                              key={i}
                              className={`text-xs px-2 py-1.5 rounded-lg font-medium transition-all hover:scale-105 ${
                                isDarkMode
                                  ? 'bg-green-900/40 text-green-300 border border-green-700/30'
                                  : 'bg-green-50 text-green-700 border border-green-200'
                              }`}
                              title={`Learned: ${word.english || word.sourceText} → ${word.darija || word.translatedText}\nTime: ${new Date(word.learned_at).toLocaleTimeString()}`}
                            >
                              <div className="font-semibold truncate">{word.english || word.sourceText}</div>
                              <div className="text-xs opacity-80 truncate">{word.darija || word.translatedText}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className={`text-center py-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {isToday ? (
                          <div>
                            <div className="text-2xl mb-1">🎯</div>
                            <div className="text-xs font-medium">{t('start_today')}</div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-xl mb-1">-</div>
                            <div className="text-xs">{t('no_words')}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-3 md:gap-4 md:justify-center">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                <div
                  key={day}
                  className={`w-full sm:w-[calc(50%-6px)] md:w-[calc(25%-12px)] md:min-h-[260px] p-4 rounded-xl border-2 ${isDarkMode ? 'border-gray-600 bg-gray-800/70' : 'border-gray-300 bg-white'}`}
                >
                  <div className={`text-sm font-bold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {day.substring(0, 3).toUpperCase()}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('no_words_yet')}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Weekly Total Summary */}
          <div className={`mt-6 p-4 rounded-xl ${
            weeklyLearning && weeklyLearning.total_this_week > 0
              ? isDarkMode ? 'bg-gradient-to-r from-green-900/30 to-blue-900/30 border border-green-500/30' : 'bg-gradient-to-r from-green-50 to-blue-50 border border-green-300'
              : isDarkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-50 border border-gray-300'
          }`}>
            <div className="flex items-center gap-3">
              <span className="text-3xl">
                {dwSettings.summary_icon_url
                  ? <img src={`${API.replace('/api', '')}${dwSettings.summary_icon_url}`} alt="" className="w-8 h-8 object-contain" />
                  : '\ud83d\udcd8'}
              </span>
              <div>
                <p className={`text-lg font-bold ${
                  weeklyLearning && weeklyLearning.total_this_week > 0
                    ? isDarkMode ? 'text-green-300' : 'text-green-700'
                    : isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {weeklyLearning && weeklyLearning.total_this_week > 0
                    ? (t('learned_words_this_week')).replace('{count}', weeklyLearning.total_this_week)
                    : (t('no_words_this_week'))
                  }
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('week_resets_sunday')}
                </p>
              </div>
            </div>
          </div>

          {/* Pro Tip */}
          <div className={`mt-6 p-4 rounded-xl ${isDarkMode ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-200'}`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl">
                {dwSettings.pro_tip_icon_url
                  ? <img src={`${API.replace('/api', '')}${dwSettings.pro_tip_icon_url}`} alt="" className="w-6 h-6 object-contain" />
                  : '\ud83d\udca1'}
              </span>
              <div>
                <p className={`text-sm font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  {t('pro_tip_title')}
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('pro_tip_message')}
                </p>
              </div>
            </div>
          </div>
        </div>
        </div>

      </div>
    </div>
  );
};

export default DailyWordsPage;
